// SPDX-License-Identifier: BUSL-1.1

/**
 * Typed API client for the cli.ceo backend.
 *
 */

import {
  clearBearerJwt,
  clearStoredToken,
  getBearerJwt,
  getStoredToken,
} from './auth';

/**
 * Base URL for all cli.ceo API calls. On production/staging builds this is
 * baked in at build time via VITE_API_URL (e.g. `https://staging.cli.ceo/api`).
 * In local dev it falls through to `/api`, which the Vite dev server proxies
 * to the Worker running on localhost.
 *
 * Exported so non-apiFetch consumers (e.g. raw `<a href>` links that open
 * JSON in a new tab) can build absolute URLs correctly. Hardcoding `/api/...`
 * in a link attribute produces a relative URL that resolves against the
 * Pages origin (`cli-ceo-staging.pages.dev`) instead of the Worker origin
 * (`staging.cli.ceo`) — and the Pages SPA fallback then serves index.html
 * with the wrong content-type.
 */
export const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

/**
 * Base URL for the standalone cli-auth Better Auth worker.
 * Falls back to API_BASE so local dev (which still proxies /api/auth/* via
 * the Vite dev server) keeps working without extra config. In CI this is
 * baked in via VITE_AUTH_URL (e.g. `https://cli-auth-staging.liftping.workers.dev`).
 *
 * Auth requests are formed as `${AUTH_BASE}/api/auth/<endpoint>` since cli-auth
 * mounts Better Auth at `/api/auth/*`.
 */
export const AUTH_BASE = import.meta.env.VITE_AUTH_URL ?? API_BASE;

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Prefer the Better Auth JWT when present (humans). Fall back to the
  // localStorage API key (CLI/agent flow) so existing API-key sign-in keeps
  // working until that path is fully retired.
  const jwt = getBearerJwt();
  const apiKey = jwt ? null : getStoredToken();
  const bearer = jwt ?? apiKey;

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include', // Send session cookies for legacy cookie-only routes.
    headers: {
      'Content-Type': 'application/json',
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      ...init?.headers,
    },
  });

  if (res.status === 401) {
    clearBearerJwt();
    clearStoredToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body}`);
  }

  return res.json();
}
